import { ensure } from '@vrplatform/utils';
import { computePosition, flip, shift } from '@floating-ui/dom';
import { posToDOMRect, ReactRenderer } from '@tiptap/react';
import type { MentionNodeAttrs } from '@tiptap/extension-mention';
import { PluginKey } from '@tiptap/pm/state';
import type { SuggestionOptions } from '@tiptap/suggestion';
import type { InfiniteDataProps } from '../../select/SelectDropdown';
import type { SelectItem } from '../../select/_types';
import { FormulaSuggestions } from './FormulaSuggestions';

type MentionItem = SelectItem;
type MentionPayload = MentionNodeAttrs;

type GetQueryData = () => InfiniteDataProps;
type SubscribeToQueryData = (listener: () => void) => () => void;

export const FORMULA_SUGGESTIONS_PLUGIN_KEY = new PluginKey(
  'formula-suggestions'
);

const isDebugEnabled = () => false;

const debugLog = (...args: unknown[]) => {
  if (!isDebugEnabled()) return;
  // biome-ignore lint/suspicious/noConsole: debug logging gated by flag
  console.debug('[FormulaSuggestions]', ...args);
};

const updatePosition = (editor: any, element: HTMLElement) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to
      ),
  };

  computePosition(virtualElement, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = 'max-content';
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
    element.style.zIndex = '400';
  });
};

const getItems = (queryData: InfiniteDataProps) =>
  queryData.data?.pages.flatMap((page) => page.list) ?? [];

export const createFormulaSuggestions = (
  getQueryData: GetQueryData,
  subscribeToQueryData: SubscribeToQueryData
) => {
  let lastQuery: string | null = null;

  return [
    ensure<Omit<SuggestionOptions<MentionItem, MentionPayload>, 'editor'>>({
      allowSpaces: true,
      allowedPrefixes: null,
      pluginKey: FORMULA_SUGGESTIONS_PLUGIN_KEY,
      items: ({ query }) => {
        const queryChanged = lastQuery !== query;
        const queryData = getQueryData();

        if (queryChanged) {
          queryData.setSearch(query);
          lastQuery = query;
        }

        debugLog('items', {
          query,
          queryChanged,
          isFetching: queryData.isFetching,
          pages: queryData.data?.pages.length ?? 0,
        });

        return getItems(queryData);
      },
      render: () => {
        let component: ReactRenderer | null = null;
        let latestProps: {
          editor: any;
          items: MentionItem[];
          command: (payload: MentionPayload) => void;
          query: string;
          clientRect?: (() => DOMRect | null) | null;
          range: { from: number; to: number };
          text?: string | null;
        } | null = null;
        let unsubscribe: (() => void) | null = null;

        const buildProps = (props: {
          editor: any;
          items: MentionItem[];
          command: (payload: MentionPayload) => void;
          query: string;
          clientRect?: (() => DOMRect | null) | null;
        }) => ({
          ...props,
          queryData: getQueryData(),
        });

        const syncQueryData = () => {
          if (!component || !latestProps) {
            return;
          }

          component.updateProps(buildProps(latestProps));
        };

        return {
          onStart: (props) => {
            latestProps = props;
            debugLog('onStart', {
              query: props.query,
              items: props.items?.length ?? 0,
              hasClientRect: !!props.clientRect,
              rangeFrom: props.range.from,
              rangeTo: props.range.to,
              text: props.text,
              selectionFrom: props.editor.state.selection.from,
              selectionTo: props.editor.state.selection.to,
              selectionEmpty: props.editor.state.selection.empty,
              isEditable: props.editor.isEditable,
              nodeBeforeText:
                props.editor.state.selection.$from.nodeBefore?.text,
            });

            component = new ReactRenderer(FormulaSuggestions, {
              props: buildProps(props),
              editor: props.editor,
            });

            unsubscribe = subscribeToQueryData(syncQueryData);

            if (!props.clientRect) {
              return;
            }

            component.element.style.position = 'absolute';
            document.body.appendChild(component.element);

            updatePosition(props.editor, component.element);
          },

          onUpdate(props) {
            latestProps = props;
            debugLog('onUpdate', {
              query: props.query,
              items: props.items?.length ?? 0,
              hasClientRect: !!props.clientRect,
              rangeFrom: props.range.from,
              rangeTo: props.range.to,
              text: props.text,
              selectionFrom: props.editor.state.selection.from,
              selectionTo: props.editor.state.selection.to,
              selectionEmpty: props.editor.state.selection.empty,
              isEditable: props.editor.isEditable,
              nodeBeforeText:
                props.editor.state.selection.$from.nodeBefore?.text,
            });

            component?.updateProps(buildProps(props));

            if (!props.clientRect || !component) {
              return;
            }

            updatePosition(props.editor, component.element);
          },

          onKeyDown(props) {
            debugLog('onKeyDown', { key: props.event.key });

            if (props.event.key === 'Escape') {
              component?.destroy();
              return true;
            }

            return component ? (component.ref as any)?.onKeyDown(props) : false;
          },

          onExit(props) {
            debugLog('onExit', {
              query: props?.query,
              text: props?.text,
              rangeFrom: props?.range?.from,
              rangeTo: props?.range?.to,
              selectionFrom: props?.editor?.state.selection.from,
              selectionTo: props?.editor?.state.selection.to,
              selectionEmpty: props?.editor?.state.selection.empty,
              isEditable: props?.editor?.isEditable,
              nodeBeforeText:
                props?.editor?.state.selection.$from.nodeBefore?.text,
            });
            unsubscribe?.();
            unsubscribe = null;
            component?.destroy();
            component = null;
            latestProps = null;
          },
        };
      },
    }),
  ];
};
